/* eslint-disable */
import { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useSound } from 'use-sound';
import { useNavigate } from 'react-router-dom';

import {
  Paper,
  InputBase,
  Tabs,
  Tab,
  Badge,
  IconButton,
  Typography,
  Tooltip,
  Switch,
  Box,
  ToggleButton,
  useTheme,
} from '@mui/material';

import {
  Group,
  MoveToInbox as MoveToInboxIcon,
  CheckBox as CheckBoxIcon,
  MessageSharp as MessageSharpIcon,
  AccessTime as ClockIcon,
  PeopleAlt as ContactsIcon,
  Search as SearchIcon,
  VolumeUpRounded as NotifyOn,
  VolumeOffRounded as NotifyOff,
  CheckCircle,
  Cancel,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  FilterAltOff,
  FilterAlt,
  PlaylistAddCheckOutlined,
} from '@mui/icons-material';

import DeleteAllConfirmationModal from './deleteAllConfirmationModal';
import TicketsList from '../TicketsListCustom';
import ContactsList from '../ContactsListCustom';
import TabPanel from '../TabPanel';
import TicketsQueueSelect from '../TicketsQueueSelect';

import alertSound from '../../assets/sound.mp3';
import openAlertSound from '../../assets/openNotify.mp3';

import { TagsFilter } from '../TagsFilter';
import { UsersFilter } from '../UsersFilter';
import { StatusFilter } from '../StatusFilter';
import { WhatsappsFilter } from '../WhatsappsFilter';

import { i18n } from '../../translate/i18n';
import { AuthContext } from '../../context/Auth/AuthContext';
import { QueueSelectedContext } from '../../context/QueuesSelected/QueuesSelectedContext';
import api from '../../services/api';
import { TicketsContext } from '../../context/Tickets/TicketsContext';
import { getStyled } from './styles';

const TicketsManagerTabs = () => {
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  const { profile } = user;
  const isAdmin =
    user.allUserChat === 'enabled' && user.profile === 'user'
      ? 'admin'
      : user.profile;
  const classes = getStyled(theme, isAdmin);

  const navigate = useNavigate();

  const [searchParam, setSearchParam] = useState('');
  const [showAllTickets, setShowAllTickets] = useState(false);

  const searchInputRef = useRef();
  const searchTimeoutRef = useRef(null);
  const [searchOnMessages, setSearchOnMessages] = useState(false);
  const { setSelectedQueuesMessage } = useContext(QueueSelectedContext);
  const { tab, setTab, tabOpen, setTabOpen } = useContext(TicketsContext);

  const [openCount, setOpenCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [groupingCount, setGroupingCount] = useState(0);

  const userQueueIds = user.queues.map((q) => q.id);
  const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState([]);
  const [forceSearch, setForceSearch] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [filter, setFilter] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [isFilterActive, setIsFilterActive] = useState(false);

  const [notifyPending, setNotifyPending] = useState(
    localStorage.getItem('notifyPending') !== 'false',
  );
  const [notifyOpen, setNotifyOpen] = useState(
    localStorage.getItem('notifyOpen') !== 'false',
  );
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertPending, setAlertPending] = useState(false);
  const [play] = useSound(alertSound);
  const [play2] = useSound(openAlertSound);
  const soundAlertRef = useRef();
  const openSoundAlertRef = useRef();

  useEffect(() => {
    setSelectedQueuesMessage(selectedQueueIds);
  }, [selectedQueueIds, setSelectedQueuesMessage]);

  useEffect(() => {
    if (
      user.profile.toUpperCase() === 'ADMIN' ||
      user.allUserChat.toUpperCase() === 'ENABLED'
    ) {
      setShowAllTickets(false);
    }
  }, [user]);

  useEffect(() => {
    if (tab === 'search') {
      searchInputRef.current?.focus();
    }
    setForceSearch((prev) => !prev);
  }, [tab]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!notifyPending || !alertPending) return;

    soundAlertRef.current = play;
    soundAlertRef.current();

    const interval = setInterval(() => soundAlertRef.current(), 10000);
    return () => clearInterval(interval);
  }, [notifyPending, alertPending, pendingCount, play]);

  useEffect(() => {
    if (notifyOpen && alertOpen) {
      openSoundAlertRef.current = play2;
      openSoundAlertRef.current();
      setAlertOpen(false);
    }
  }, [notifyOpen, alertOpen, play2]);

  useEffect(() => {
    if (['open', 'pending', 'contacts'].includes(tabOpen)) {
      setTab('open');
    } else if (['closed', 'canceled'].includes(tabOpen)) {
      setTab('closed');
    } else if (tabOpen === 'group') {
      setTab('group');
    }
  }, [tabOpen, setTab]);

  const scheduleSearch = useCallback((fn, delay = 500) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(fn, delay);
  }, []);

  const triggerForceSearch = useCallback(() => {
    setForceSearch((prev) => !prev);
  }, []);

  const handleSearch = useCallback(
    (e) => {
      const term = e.target.value.toLowerCase();

      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

      if (term === '') {
        setSearchParam('');
        triggerForceSearch();
        setTab('open');
        return;
      }

      if (tab !== 'search') {
        openFilterPanel();
        setTab('search');
      }

      scheduleSearch(() => {
        setSearchParam(term);
        triggerForceSearch();
      });
    },
    [tab, setTab, scheduleSearch, triggerForceSearch],
  );

  const openFilterPanel = useCallback(() => setFilter(true), []);

  const handleFilter = useCallback(() => {
    setFilter((prev) => {
      if (prev) setTab('open');
      else setTab('search');
      return !prev;
    });
  }, [setTab]);

  const makeFilterHandler = useCallback(
    (setter, mapper = (items) => items.map((t) => t.id)) =>
      (selecteds) => {
        const values = mapper(selecteds);

        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

        if (values.length === 0) {
          triggerForceSearch();
        } else if (tab !== 'search') {
          setTab('search');
        }

        scheduleSearch(() => {
          setter(values);
          triggerForceSearch();
        });
      },
    [tab, setTab, scheduleSearch, triggerForceSearch],
  );

  const handleSelectedTags = makeFilterHandler(setSelectedTags);
  const handleSelectedUsers = makeFilterHandler(setSelectedUsers);
  const handleSelectedWhatsapps = makeFilterHandler(setSelectedWhatsapp);
  const handleSelectedStatus = makeFilterHandler(setSelectedStatus, (items) =>
    items.map((t) => t.status),
  );

  const handleBack = useCallback(() => navigate('/tickets'), [navigate]);

  const handleChangeTab = useCallback(
    (_, newValue) => setTab(newValue),
    [setTab],
  );

  const handleChangeTabOpen = useCallback(
    (_, newValue) => {
      handleBack();
      setTabOpen(newValue);
    },
    [handleBack, setTabOpen],
  );

  const applyPanelStyle = useCallback(
    (tabStatus) => (tabOpen !== tabStatus ? { display: 'none' } : {}),
    [tabOpen],
  );

  const CloseAllTicket = useCallback(async () => {
    try {
      await api.post('/tickets/closeAll', {
        status: 'canceled',
        selectedQueueIds,
        userId: user.id,
      });
      setConfirmationOpen((prev) => !prev);
    } catch (err) {
      console.error('Error: ', err);
    }
  }, [selectedQueueIds, user.id]);

  return (
    <Paper elevation={0} variant='outlined' style={classes.ticketsWrapper}>
      <div style={classes.serachInputWrapper}>
        <SearchIcon style={classes.searchIcon} />
        <InputBase
          sx={theme.custom.textField}
          inputRef={searchInputRef}
          placeholder={i18n.t('tickets.search.placeholder')}
          type='search'
          onChange={handleSearch}
        />
        <Tooltip
          placement='top'
          title='Marque para pesquisar também nos conteúdos das mensagens (mais lento)'
        >
          <div>
            <Switch
              size='small'
              checked={searchOnMessages}
              onChange={(e) => setSearchOnMessages(e.target.checked)}
              sx={theme.custom.switch}
            />
          </div>
        </Tooltip>
        <IconButton
          sx={classes.filterIcon}
          variant='contained'
          aria-label='filter'
          onClick={() => {
            setIsFilterActive((prev) => !prev);
            handleFilter();
          }}
        >
          {isFilterActive ? (
            <FilterAlt style={classes.icon} />
          ) : (
            <FilterAltOff style={classes.icon} />
          )}
        </IconButton>
      </div>

      {filter && (
        <div style={{ marginTop: theme.spacing(-1) }}>
          <TagsFilter onFiltered={handleSelectedTags} />
          <WhatsappsFilter onFiltered={handleSelectedWhatsapps} />
          <StatusFilter onFiltered={handleSelectedStatus} />
          {profile === 'admin' && (
            <>
              <UsersFilter onFiltered={handleSelectedUsers} />
              <TicketsQueueSelect
                selectedQueueIds={selectedQueueIds}
                userQueues={user?.queues}
                onChange={(values) => setSelectedQueueIds(values)}
              />
            </>
          )}
        </div>
      )}

      <Paper square elevation={0} style={classes.ticketOptionsBox}>
        <Box style={classes.boxOptions} display='flex'>
          {/* Botão "Todos" */}
          {user.profile === 'admin' && (
            <ToggleButton
              style={{
                ...classes.button,
                opacity: showAllTickets ? '1' : '0.4',
              }}
              value='uncheck'
              selected={showAllTickets}
              onChange={() => setShowAllTickets((prev) => !prev)}
            >
              <Box
                display='flex'
                flexDirection='column'
                alignItems='center'
                justifyContent='center'
              >
                {showAllTickets ? (
                  <VisibilityIcon style={classes.icon} />
                ) : (
                  <VisibilityOffIcon style={classes.icon} />
                )}
                <span style={classes.buttonText}>Todos</span>
              </Box>
            </ToggleButton>
          )}

          {/* Botão "Fechar Todos" (Admin) */}
          {user.profile === 'admin' && (
            <ToggleButton
              style={classes.button}
              onClick={() => setConfirmationOpen((prev) => !prev)}
              value='fecharTodos'
            >
              <Box
                display='flex'
                flexDirection='column'
                alignItems='center'
                justifyContent='center'
              >
                <PlaylistAddCheckOutlined
                  style={{
                    ...classes.icon,
                    transform: 'translateX(20%) scale(1.4)',
                  }}
                />
                <span style={classes.buttonText}>Fechar Todos</span>
              </Box>
            </ToggleButton>
          )}

          {/* Botão "Abertos" */}
          <ToggleButton
            style={{ ...classes.button, opacity: tab === 'open' ? '1' : 0.4 }}
            onClick={() => {
              setTabOpen('pending');
              handleChangeTab(null, 'open');
            }}
            value='openTickets'
          >
            <Box
              display='flex'
              flexDirection='column'
              alignItems='center'
              justifyContent='center'
            >
              <MoveToInboxIcon style={classes.icon} />
              <span style={classes.buttonText}>Abertos</span>
            </Box>
          </ToggleButton>

          {/* Botão "Resolvidos" */}
          <ToggleButton
            style={{
              ...classes.button,
              opacity: tab === 'closed' ? '1' : '0.4',
            }}
            onClick={() => {
              setTabOpen('closed');
              handleChangeTab(null, 'closed');
            }}
            value='resolvedTickets'
          >
            <Box
              display='flex'
              flexDirection='column'
              alignItems='center'
              justifyContent='center'
            >
              <CheckBoxIcon style={classes.icon} />
              <span style={classes.buttonText}>Resolvidos</span>
            </Box>
          </ToggleButton>

          {/* --- BOTÃO GRUPOS --- */}
          {user.allowGroup && (
            <ToggleButton
              style={{
                ...classes.button,
                opacity: tabOpen === 'group' ? '1' : '0.4',
              }}
              value='group'
              onClick={() => {
                handleChangeTab(null, 'group');
                setTabOpen('group');
              }}
            >
              <Box
                display='flex'
                flexDirection='column'
                alignItems='center'
                justifyContent='center'
              >
                <Badge
                  overlap='rectangular'
                  classes={{ badge: classes.customBadge }}
                  badgeContent={groupingCount}
                  color='primary'
                >
                  <Box
                    display='flex'
                    flexDirection='column'
                    alignItems='center'
                    justifyContent='center'
                  >
                    <Group
                      style={classes.icon}
                      svg={'height: 20px, width: 20px'}
                    />
                    <span style={classes.buttonText}>Grupos</span>
                  </Box>
                </Badge>
              </Box>
            </ToggleButton>
          )}

          <DeleteAllConfirmationModal
            confirmationOpen={confirmationOpen}
            setConfirmationOpen={setConfirmationOpen}
            CloseAllTicket={CloseAllTicket}
          />
        </Box>
      </Paper>

      {tab === 'open' && (
        <TabPanel
          name='open'
          value={'open'}
          style={classes.ticketsWrapperContacts}
        >
          <Tabs
            value={tabOpen}
            onChange={handleChangeTabOpen}
            sx={classes.tab}
            variant='fullWidth'
          >
            {/* ABERTO */}
            <Tab
              sx={classes.customTab}
              label={
                <Box position='absolute' display='flex'>
                  <div
                    style={classes.customNotifyIcon}
                    onClick={() => {
                      const next = !notifyOpen;
                      setNotifyOpen(next);
                      localStorage.setItem('notifyOpen', next);
                    }}
                  >
                    {notifyOpen ? (
                      <NotifyOn
                        style={{
                          ...classes.filterIcon,
                          color: theme.custom.colors.confirmButton,
                          marginLeft: 4,
                        }}
                      />
                    ) : (
                      <NotifyOff
                        style={{
                          ...classes.filterIcon,
                          color: theme.custom.colors.cancelButton,
                          marginLeft: 4,
                        }}
                      />
                    )}
                  </div>

                  <Box style={classes.customBox}>
                    <MessageSharpIcon style={classes.customIcon} />
                    <Typography style={classes.customText}>
                      {i18n.t('ticketsList.assignedHeader')}
                    </Typography>
                  </Box>
                  <Badge
                    overlap='circular'
                    sx={classes.customBadge}
                    badgeContent={openCount}
                  />
                </Box>
              }
              value={'open'}
              name='open'
              classes={{ root: classes.tabPanelItem }}
            />

            {/* PENDENTE */}
            <Tab
              sx={classes.customTab}
              label={
                <Box
                  position='absolute'
                  display='flex'
                  style={{
                    transform:
                      pendingCount > 0 ? 'translateX(-6%)' : 'translateX(1%)',
                  }}
                >
                  <div
                    style={classes.customNotifyIcon}
                    onClick={() => {
                      const next = !notifyPending;
                      setNotifyPending(next);
                      localStorage.setItem('notifyPending', next);
                    }}
                  >
                    {notifyPending ? (
                      <NotifyOn
                        style={{
                          ...classes.filterIcon,
                          color: theme.custom.colors.confirmButton,
                          marginLeft: 4,
                        }}
                      />
                    ) : (
                      <NotifyOff
                        style={{
                          ...classes.filterIcon,
                          color: theme.custom.colors.cancelButton,
                          marginLeft: 4,
                        }}
                      />
                    )}
                  </div>
                  <Box style={classes.customBox}>
                    <ClockIcon style={classes.customIcon} />
                    <Typography style={classes.customText}>
                      {i18n.t('ticketsList.pendingHeader')}
                    </Typography>
                  </Box>
                  {pendingCount > 0 && (
                    <Badge
                      overlap='circular'
                      sx={classes.customBadge}
                      badgeContent={pendingCount}
                    />
                  )}
                </Box>
              }
              value={'pending'}
              name='pending'
              classes={{ root: classes.tabPanelItem }}
            />

            {/* CONTATOS */}
            <Tab
              label={
                <Box style={classes.customBox}>
                  <ContactsIcon style={classes.customIcon} />
                  <Typography style={classes.customText}>
                    {i18n.t('ticketsList.contacts')}
                  </Typography>
                </Box>
              }
              value={'contacts'}
              name='contacts'
              style={classes.tabPanelItem}
            />
          </Tabs>

          <Paper style={classes.ticketsWrapperContacts}>
            <TicketsList
              status='open'
              showAll={showAllTickets}
              selectedQueueIds={selectedQueueIds}
              updateCount={(val) => setOpenCount(val)}
              style={applyPanelStyle('open')}
              setAlertOpen={setAlertOpen}
              setAlertPending={setAlertPending}
            />
            <TicketsList
              status='pending'
              selectedQueueIds={selectedQueueIds}
              showAll={
                user.profile === 'admin' || user.allUserChat === 'enabled'
                  ? showAllTickets
                  : false
              }
              updateCount={(val) => setPendingCount(val)}
              style={applyPanelStyle('pending')}
              setAlertOpen={setAlertOpen}
              setAlertPending={setAlertPending}
            />
            <ContactsList
              status='contacts'
              tabOpen={tabOpen}
              style={applyPanelStyle('contacts')}
            />
          </Paper>
        </TabPanel>
      )}

      {/* --- RESOLVIDOS --- */}
      {tab === 'closed' && (
        <TabPanel name='closed' value={'closed'} style={classes.ticketsWrapper}>
          <Tabs
            value={tabOpen}
            onChange={handleChangeTabOpen}
            sx={classes.tab}
            variant='fullWidth'
          >
            {/* TICKETS RESOLVIDOS */}
            <Tab
              label={
                <Box style={classes.customBox}>
                  <CheckCircle style={classes.customIcon} />
                  <Typography style={classes.customText}>
                    {i18n.t('ticketsList.resolved')}
                  </Typography>
                </Box>
              }
              value={'closed'}
              name='closed'
              style={classes.tabPanelItem}
            />
            {/* TICKETS CANCELADOS */}
            <Tab
              label={
                <Box style={classes.customBox}>
                  <Cancel style={classes.customIcon} />
                  <Typography style={classes.customText}>
                    {i18n.t('ticketsList.canceled')}
                  </Typography>
                </Box>
              }
              value={'canceled'}
              name='canceled'
              style={classes.tabPanelItem}
            />
          </Tabs>
          <Paper style={classes.ticketsWrapper}>
            <TicketsList
              status='closed'
              showAll={showAllTickets}
              selectedQueueIds={selectedQueueIds}
              updateCount={(val) => setOpenCount(val)}
              style={applyPanelStyle('closed')}
              setAlertOpen={setAlertOpen}
              setAlertPending={setAlertPending}
            />
            <TicketsList
              status='canceled'
              showAll={showAllTickets}
              selectedQueueIds={selectedQueueIds}
              updateCount={(val) => setPendingCount(val)}
              style={applyPanelStyle('canceled')}
              setAlertOpen={setAlertOpen}
              setAlertPending={setAlertPending}
            />
          </Paper>
        </TabPanel>
      )}

      {tab === 'search' && (
        <TabPanel name='search' value={'search'} style={classes.ticketsWrapper}>
          {profile === 'admin' && (
            <>
              <TicketsList
                statusFilter={selectedStatus}
                searchParam={searchParam}
                showAll={showAllTickets}
                tags={selectedTags}
                users={selectedUsers}
                selectedQueueIds={selectedQueueIds}
                whatsappIds={selectedWhatsapp}
                forceSearch={forceSearch}
                searchOnMessages={searchOnMessages}
                status='search'
                setAlertOpen={setAlertOpen}
                setAlertPending={setAlertPending}
              />
            </>
          )}

          {profile === 'user' && (
            <TicketsList
              statusFilter={selectedStatus}
              searchParam={searchParam}
              showAll={false}
              tags={selectedTags}
              selectedQueueIds={selectedQueueIds}
              whatsappIds={selectedWhatsapp}
              forceSearch={forceSearch}
              searchOnMessages={searchOnMessages}
              status='search'
              setAlertOpen={setAlertOpen}
              setAlertPending={setAlertPending}
            />
          )}
        </TabPanel>
      )}

      {tab === 'group' && (
        <TabPanel
          name='group'
          value={'group'}
          style={classes.ticketsWrapperContacts}
        >
          <Tabs
            value={tabOpen}
            onChange={handleChangeTabOpen}
            sx={classes.tab}
            variant='fullWidth'
          >
            <Tab
              label={
                <Box position='absolute' display='flex'>
                  <Box style={classes.customBox}>
                    <Group style={classes.customIcon} />
                    <Typography style={classes.customText}>
                      {i18n.t('ticketsList.groupingHeader')}
                    </Typography>
                  </Box>
                </Box>
              }
              value={'group'}
              name='group'
              style={classes.tabPanelItem}
            />
          </Tabs>
          <Paper style={classes.ticketsWrapperContacts}>
            {user.allowGroup && (
              <TicketsList
                status='group'
                showAll={showAllTickets}
                selectedQueueIds={selectedQueueIds}
                updateCount={(val) => setGroupingCount(val)}
                style={applyPanelStyle('group')}
                setAlertOpen={setAlertOpen}
                setAlertPending={setAlertPending}
              />
            )}
          </Paper>
        </TabPanel>
      )}
    </Paper>
  );
};

export default TicketsManagerTabs;
